import { createElement, type ReactNode } from "react";

/**
 * Use Typography for all headings and body text so colors and styles
 * follow the app theme (light/dark) automatically. Prefer variant="h1" | "h2" | "h3"
 * for page and section headings instead of raw <h1>/<h2>/<h3> with custom classes.
 */
type TypographyVariant =
  | "h1"
  | "h2"
  | "h3"
  | "body"
  | "bodySmall"
  | "helper"
  | "caption"
  | "label";

type IntrinsicTag = keyof React.JSX.IntrinsicElements;

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: TypographyVariant;
  as?: IntrinsicTag;
  children: ReactNode;
  className?: string;
}

const variantClasses: Record<TypographyVariant, string> = {
  h1: "text-2xl font-bold text-slate-900 dark:text-slate-100",
  h2: "text-lg font-semibold text-slate-900 dark:text-slate-100",
  h3: "text-base font-semibold text-slate-900 dark:text-slate-100",
  body: "text-sm text-slate-700 dark:text-slate-200",
  bodySmall: "text-sm text-slate-600 dark:text-slate-300",
  helper:
    "text-xs text-slate-500 dark:text-slate-400 leading-relaxed",
  caption: "text-xs text-slate-500 dark:text-slate-400",
  label: "text-sm font-medium text-slate-700 dark:text-slate-300",
};

const defaultTag: Record<TypographyVariant, IntrinsicTag> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  body: "p",
  bodySmall: "p",
  helper: "p",
  caption: "span",
  label: "span",
};

export function Typography({
  variant = "body",
  as,
  children,
  className = "",
  ...rest
}: TypographyProps) {
  const tag: IntrinsicTag = as ?? defaultTag[variant];
  const classes = `${variantClasses[variant]} ${className}`.trim();
  return createElement(tag, { className: classes, ...rest }, children);
}
